import React, { useState, useEffect, useRef } from 'react';
import { Check, AlertTriangle } from 'lucide-react';

/**
 * Google Places address autocomplete.
 *
 * Exists to fix a data problem at the source. Free-typed addresses cannot be
 * matched against county parcel records reliably — abbreviations vary
 * (`Black Oak Ridge Road` vs `BLACK OAK RIDGE RD`), spacing varies
 * (`Quail Wood` vs `QUAILWOOD`), and typos happen (`Las Palmes` for
 * `Las Palmas`). Parsing that after the fact is guesswork.
 *
 * Picking from this list stores the canonical, postal-standard form instead, so
 * the address matches county records exactly and no downstream parsing is
 * needed.
 *
 * Runs in the browser against a referrer-restricted key. If the key is absent
 * it degrades to a plain text input rather than blocking the form.
 */

export interface ResolvedAddress {
  /** house number + abbreviated street, e.g. "27600 Black Oak Ridge Rd" */
  street: string;
  city: string;
  state: string;
  zip: string;
  /** County with the trailing " County" removed, e.g. "Henry" */
  county: string;
  formatted: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onResolved: (address: ResolvedAddress) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  name?: string;
}

const KEY = import.meta.env.VITE_GOOGLE_PLACES_KEY as string | undefined;

// Google bills autocomplete per *session*, not per keystroke — but only when the
// session is closed by a Place Details call carrying the same token. Reusing a
// token, or never closing the session, reverts every keystroke to per-request
// billing.
const newToken = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function AddressAutocomplete({
  value,
  onChange,
  onResolved,
  placeholder,
  required,
  className,
  name,
}: Props) {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  // Google's coverage of addressable US properties is effectively complete, so
  // zero suggestions for a plausible-looking address means the address is
  // probably wrong. Catching that here beats discovering it weeks later when
  // comping finds no parcel record.
  const [notFound, setNotFound] = useState(false);
  const token = useRef(newToken());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const box = useRef<HTMLDivElement>(null);

  // Close the dropdown on an outside click, or it hangs over the rest of the form.
  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setSuggestions([]);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  useEffect(() => {
    if (!KEY || confirmed || value.length < 4) {
      setSuggestions([]);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void suggest(value), 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, confirmed]);

  const suggest = async (input: string) => {
    try {
      const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': KEY as string },
        body: JSON.stringify({
          input,
          sessionToken: token.current,
          includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
          includedRegionCodes: ['us'],
        }),
      });
      if (!res.ok) {
        // 403 here is almost always the key's referrer restriction not covering
        // this domain. Naming it beats an empty dropdown.
        setError(
          res.status === 403
            ? 'Address suggestions unavailable for this domain. You can still type the address.'
            : ''
        );
        setSuggestions([]);
        return;
      }
      setError('');
      const data = await res.json();
      const found = data.suggestions || [];
      setSuggestions(found);
      // Only warn once the input looks like a real attempt — a house number
      // plus a street name. Warning mid-word would fire on every address.
      const looksComplete = /^\s*\d+\s+\S+/.test(input) && input.trim().length >= 8;
      setNotFound(found.length === 0 && looksComplete);
    } catch {
      setSuggestions([]);
    }
  };

  const pick = async (s: any) => {
    const placeId = s.placePrediction?.placeId;
    if (!placeId) return;
    setSuggestions([]);
    try {
      const res = await fetch(
        `https://places.googleapis.com/v1/places/${placeId}?sessionToken=${token.current}`,
        {
          headers: {
            'X-Goog-Api-Key': KEY as string,
            'X-Goog-FieldMask': 'formattedAddress,addressComponents',
          },
        }
      );
      if (!res.ok) throw new Error();
      const place = await res.json();

      // shortText is the abbreviated postal form — "Black Oak Ridge Rd", which
      // is what county records hold. longText would give "…Road" and not match.
      const part = (type: string, long = false) => {
        const c = place.addressComponents?.find((x: any) => x.types?.includes(type));
        return (long ? c?.longText : c?.shortText) || '';
      };

      const number = part('street_number');
      const route = part('route');
      const county = part('administrative_area_level_2', true).replace(/\s+County$/i, '');

      const resolved: ResolvedAddress = {
        street: [number, route].filter(Boolean).join(' '),
        city: part('locality', true) || part('sublocality', true),
        state: part('administrative_area_level_1'),
        zip: part('postal_code'),
        county,
        formatted: place.formattedAddress || '',
      };

      onChange(resolved.street);
      onResolved(resolved);
      setConfirmed(true);
    } catch {
      setError('Could not load that address. You can type it manually.');
    } finally {
      // Fresh token for the next search; reusing one breaks session billing.
      token.current = newToken();
    }
  };

  return (
    <div ref={box} className="relative">
      <div className="relative">
        <input
          type="text"
          name={name}
          value={value}
          required={required}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => {
            // Any edit invalidates the confirmed selection — the text no longer
            // matches what Google returned, so it must not stay marked verified.
            setConfirmed(false);
            setNotFound(false);
            onChange(e.target.value);
          }}
          className={className}
        />
        {confirmed && (
          <span
            className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600"
            title="Address verified — stored in postal-standard form"
          >
            <Check className="w-5 h-5" />
          </span>
        )}
      </div>

      {suggestions.length > 0 && (
        <ul
          className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200
                     rounded-xl shadow-lg max-h-64 overflow-y-auto"
        >
          {suggestions.map((s) => (
            <li key={s.placePrediction?.placeId}>
              <button
                type="button"
                onClick={() => void pick(s)}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
              >
                {s.placePrediction?.text?.text}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* A warning, never a block. A partner may have a legitimately
          unlistable property, and refusing the submission would be worse than
          accepting an address we cannot verify. */}
      {notFound && !confirmed && suggestions.length === 0 && (
        <p className="mt-1 text-xs text-amber-700 flex items-start gap-1">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            We couldn't find this address. Please double-check it — an address
            we can't verify won't return property data. You can still submit.
          </span>
        </p>
      )}

      {error && <p className="mt-1 text-xs text-gray-500">{error}</p>}
    </div>
  );
}
